<template>
  <b-modal
    v-model="modal"
    title="Vincular acompanhante existente"
    size="lg"
    :no-enforce-focus="true"
    :no-close-on-backdrop="true"
    @hidden="reset"
  >
    <loading
      :active.sync="loading"
      color="#556ee6"
      loader="dots"
      :can-cancel="false"
      :is-full-page="false"
    />

    <p class="text-muted mb-3">
      Selecione uma inscrição já existente no evento para transformá-la em acompanhante deste participante.
      Você pode vincular vários acompanhantes: a cada vínculo a lista é atualizada e você seleciona o próximo.
      Apenas inscrições independentes (sem vínculo e sem acompanhantes próprios) são listadas.
      Inscrições com pedido pago não podem ser vinculadas.
    </p>

    <div class="form-group">
      <label>Buscar por nome, e-mail ou CPF</label>
      <multiselect
        v-model="selected"
        :options="candidates"
        :loading="searching"
        :internal-search="false"
        :clear-on-select="false"
        :close-on-select="true"
        :show-no-results="true"
        track-by="id"
        label="name"
        placeholder="Digite para buscar..."
        select-label="Selecionar"
        selected-label="Selecionado"
        deselect-label=""
        @search-change="onSearchChange"
        @open="ensureLoaded"
      >
        <template slot="option" slot-scope="{ option }">
          <div>
            <strong>{{ option.name }}</strong>
            <small class="d-block text-muted">
              {{ option.email }}
              <span v-if="option.group && option.group.data"> · {{ option.group.data.name }}</span>
            </small>
          </div>
        </template>
        <template slot="noResult">Nenhuma inscrição elegível encontrada.</template>
        <template slot="noOptions">Digite para buscar inscrições disponíveis.</template>
      </multiselect>
    </div>

    <div v-if="selected" class="card border-gray mt-3">
      <div class="card-body">
        <h6 class="mb-2">
          <i class="bx bx-user-circle font-size-18 align-middle text-primary mr-1"></i>
          {{ selected.name }}
        </h6>
        <div class="row text-sm">
          <div class="col-md-6">
            <small class="text-muted d-block">E-mail</small>
            <span>{{ selected.email || '—' }}</span>
          </div>
          <div class="col-md-6">
            <small class="text-muted d-block">Telefone</small>
            <span>{{ selected.phone || '—' }}</span>
          </div>
          <div class="col-md-6 mt-2">
            <small class="text-muted d-block">CPF</small>
            <span>{{ selected.cpf || '—' }}</span>
          </div>
          <div class="col-md-6 mt-2">
            <small class="text-muted d-block">Grupo atual → futuro</small>
            <span>
              {{ selected.group && selected.group.data ? selected.group.data.name : '—' }}
              <i class="mdi mdi-arrow-right mx-1"></i>
              <b>ACOMPANHANTE</b>
            </span>
          </div>
        </div>
      </div>
    </div>

    <template v-slot:modal-footer>
      <b-button variant="secondary" @click="close">Fechar</b-button>
      <b-button
        variant="primary"
        :disabled="!selected || saving"
        @click="confirm"
      >
        <i class="mdi mdi-link-variant mr-1"></i>
        Vincular
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'
import Multiselect from 'vue-multiselect'
import Swal from 'sweetalert2'
import { getAvailableForLinking, linkCompanion } from './services'
import { errorMessage, successMessage } from '@/components/alertsMessages'

export default {
  components: { Loading, Multiselect },
  props: {
    mainParticipantId: {
      type: [Number, String],
      default: null
    }
  },
  data () {
    return {
      modal: false,
      loading: false,
      searching: false,
      saving: false,
      selected: null,
      candidates: [],
      lastQuery: '',
      searchTimer: null
    }
  },
  methods: {
    open () {
      this.modal = true
      this.candidates = []
      this.selected = null
      this.lastQuery = ''
    },
    close () {
      this.modal = false
    },
    reset () {
      this.selected = null
      this.candidates = []
      this.lastQuery = ''
    },
    ensureLoaded () {
      if (this.candidates.length === 0 && !this.searching) {
        this.search('')
      }
    },
    onSearchChange (query) {
      if (this.searchTimer) clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => this.search(query || ''), 350)
    },
    async search (query) {
      if (!this.mainParticipantId) return
      this.lastQuery = query
      this.searching = true
      try {
        const res = await getAvailableForLinking({
          main_participant_id: this.mainParticipantId,
          search: query,
          per_page: 20
        })
        // paginator do Laravel retorna { data: [...], current_page, ... }
        this.candidates = (res.data && res.data.data) ? res.data.data : (res.data || [])
      } catch (e) {
        errorMessage('Falha ao buscar candidatos.')
        this.candidates = []
      } finally {
        this.searching = false
      }
    },
    async confirm () {
      if (!this.selected) return
      const r = await Swal.fire({
        title: 'Vincular acompanhante?',
        html: `<b>${this.selected.name}</b> será vinculado como acompanhante e seu grupo passará a ser <b>ACOMPANHANTE</b>. Esta ação pode ser revertida.`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#388741',
        cancelButtonColor: '#4a4848',
        confirmButtonText: 'SIM, VINCULAR',
        cancelButtonText: 'CANCELAR'
      })
      if (!r.value) return

      this.saving = true
      try {
        await linkCompanion({
          main_participant_id: this.mainParticipantId,
          candidate_participant_id: this.selected.id
        })
        successMessage('Acompanhante vinculado com sucesso.', 2000)
        this.$emit('linked')
        // Mantém o modal aberto para vincular vários (um a um); o recém-vinculado
        // sai da lista de elegíveis ao recarregar.
        this.selected = null
        this.search(this.lastQuery)
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || 'Falha ao vincular acompanhante.'
        errorMessage('Alerta: ' + msg)
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.text-sm { font-size: 13px; }
.border-gray { border: 1px solid #e9e9e9; }
</style>
